<script setup lang="ts">
import { computed, inject, onMounted, ref, watch, type PropType } from 'vue'
import { useRoute } from 'vue-router'
import { useHttp, useTranslate } from '@/shared/lib'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import { useMandatoryFieldError } from '../../model/useMandatoryFieldError'
import {
  buildLinkSelectMenuProps,
  linkSelectMenuPropsKey,
} from '../../model/linkSelectMenuProps'

const props = defineProps({
  field: {
    type: Object as PropType<ObjectInventoryField>,
    default: () => ({}),
  },
  label: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  hideDetails: {
    type: Boolean,
    default: true,
  },
  mandatory: {
    default: false,
  },
  showMandatoryError: {
    type: Boolean,
    default: false,
  },
  object_type: {
    type: [Number, String],
    default: undefined,
  },
})

const value = defineModel<string | number | null>({ default: null })

const parentMenuProps = inject(linkSelectMenuPropsKey, null)
const menuProps = computed(() => buildLinkSelectMenuProps(parentMenuProps))

const route = useRoute()
const http = useHttp()
const { trs } = useTranslate()

const groupId = computed(() => String(route.params.object_id ?? ''))
const objectTypeId = computed(() => Number(props.field?.object_type) || Number(props.object_type) || 0)

type LinkItem = { object_list_id: number; name: string }

const searchLoading = ref(false)
const searchItems = ref<LinkItem[]>([])
const selectedCache = ref<LinkItem | null>(null)

async function fetchItems(query: string) {
  if (!groupId.value || !objectTypeId.value) return
  searchLoading.value = true
  try {
    const params = new URLSearchParams()
    if (query.trim()) params.set('search', query.trim())
    params.set('page', '1')
    params.set('per_page', '50')
    const url = `/inventory/objects/${groupId.value}/inventory-types/${objectTypeId.value}/elements?${params}`
    const res = await http.get(url)
    const rows = (res.data ?? []) as any[]
    searchItems.value = rows.map((row: any) => ({
      object_list_id: Number(row.object_list_id),
      name: String(row.name ?? ''),
    }))
  } finally {
    searchLoading.value = false
  }
}

async function fetchSelectedName(id: string | number) {
  if (id === null || id === undefined || id === '') {
    selectedCache.value = null
    return
  }
  const alreadyInItems = searchItems.value.some((it) => String(it.object_list_id) === String(id))
  if (alreadyInItems) return
  try {
    const params = new URLSearchParams({ object_ids: String(id) })
    const res = await http.get(`/inventory/objects/passport?${params}`)
    const rows = (res.data ?? []) as any[]
    const row = rows[0]
    if (row) {
      selectedCache.value = {
        object_list_id: Number(row.id ?? row.object_list_id),
        name: String(row.name ?? trs('itf.unknown_object')),
      }
    }
  } catch {
    selectedCache.value = null
  }
}

let searchTimeout: ReturnType<typeof setTimeout> | null = null
function onSearchInput(query: string) {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => fetchItems(query), 300)
}

watch(
  () => value.value,
  (v) => {
    if (v !== null && v !== undefined && v !== '') {
      fetchSelectedName(v)
    } else {
      selectedCache.value = null
    }
  },
  { immediate: true },
)

const {
  visible: mandatoryErrorVisible,
  message: mandatoryErrorMessage,
  markTouched,
  registerUserInteraction,
} = useMandatoryFieldError(
  () => ({
    showMandatoryError: props.showMandatoryError,
    mandatory: props.mandatory,
    type: 'object_link',
  }),
  () => value.value,
)

function onBlur() {
  markTouched()
}

function onMenuToggle(open: boolean) {
  if (open) {
    markTouched()
  }
}

onMounted(() => {
  if (groupId.value && objectTypeId.value) {
    fetchItems('')
  }
})
</script>

<template>
  <VAutocomplete
    v-model="value"
    :items="searchItems"
    item-title="name"
    item-value="object_list_id"
    no-filter
    :label="label"
    :disabled="disabled || !objectTypeId"
    :loading="loading || searchLoading"
    :readonly="readonly"
    :hide-details="mandatoryErrorVisible ? false : hideDetails"
    :error="mandatoryErrorVisible ? true : undefined"
    :error-messages="mandatoryErrorVisible ? mandatoryErrorMessage : undefined"
    clearable
    :menu-props="menuProps"
  />
</template>
